import { dbService } from './supabase';

/**
 * Comprehensive synonym dictionary for pharmaceutical and manufacturing terms
 * Handles abbreviations, regional variations, and common alternative spellings
 */

// Text normalization utilities
export const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ') // Replace special characters with spaces
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
};

// Remove common words that don't add meaning to matching
const STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
  'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the',
  'to', 'was', 'will', 'with', 'services', 'support', 'solutions'
]);

export const removeStopWords = (text: string): string => {
  return text
    .split(' ')
    .filter(word => !STOP_WORDS.has(word) && word.length > 1)
    .join(' ');
};

// The SYNONYM_MAP will be populated dynamically from the database.
export const SYNONYM_MAP = new Map<string, string>();
let synonymsInitialized = false;

/**
 * Fetches synonyms from the database and populates the SYNONYM_MAP.
 * This should be called once when the application starts.
 */
export const initializeSynonyms = async () => {
  // Prevent re-initialization
  if (synonymsInitialized) return;

  const groups = await dbService.getSynonymGroups();

  if (!groups || groups.length === 0) {
    console.error("Synonym groups could not be loaded from the database.");
    return;
  }
  // Build the reverse mapping
  for (const group of groups) {
    const canonical = group.canonical_term;
    if (!canonical || !group.synonyms) continue;

    // Also map the canonical term to itself after normalization
    const normalizedCanonical = normalizeText(canonical);
    SYNONYM_MAP.set(normalizedCanonical, canonical);

    for (const synonym of group.synonyms) {
      const normalizedSynonym = normalizeText(synonym);
      SYNONYM_MAP.set(normalizedSynonym, canonical);
    }
  }

  synonymsInitialized = true;
};

/**
 * Normalize a term using the synonym dictionary
 * @param term - The term to normalize
 * @returns The canonical form of the term or the original if no match found
 */
export const normalizeTerm = (term: string): string => {
  const normalized = normalizeText(term);
  const canonical = SYNONYM_MAP.get(normalized);
  return canonical || normalized;
};

/**
 * Get all possible variations of a term including synonyms
 * @param term - The term to get variations for
 * @returns Array of all possible variations
 */
export const getTermVariations = (term: string): string[] => {
  const normalized = normalizeText(term);
  const canonical = SYNONYM_MAP.get(normalized);
  
  // This function's behavior will change as we no longer have SYNONYM_GROUPS.
  // For now, it will be less effective but the core matching still works.
  // A more advanced implementation could fetch the group if needed.
  if (canonical) {
    // Return the canonical term as a representation of the group
    return [canonical];
  }
  
  return [term];
};


/**
 * Check if two terms are synonymous
 * @param term1 - First term
 * @param term2 - Second term
 * @returns True if the terms are synonymous
 */
export const areSynonymous = (term1: string, term2: string): boolean => {
  const canonical1 = normalizeTerm(term1);
  const canonical2 = normalizeTerm(term2);
  return canonical1 === canonical2;
};

/**
 * Normalize an array of terms
 * @param terms - Array of terms to normalize
 * @returns Array of normalized canonical terms
 */
export const normalizeTerms = (terms: string[]): string[] => {
  return terms.map(term => normalizeTerm(term));
};

/**
 * Preprocess text for fuzzy matching by normalizing and removing stop words
 * @param text - Text to preprocess
 * @returns Preprocessed text
 */
export const preprocessForMatching = (text: string): string => {
  const normalized = normalizeText(text);
  const withoutStopWords = removeStopWords(normalized);
  return withoutStopWords;
}; 